import React from 'react';
import { RemoveCircleOutline, AddCircleOutline, OpenInNew } from '@mui/icons-material';
import { getAllItems, saveItem } from '../../sp-firebase/genericFB';
import { query, where } from 'firebase/firestore';
import { useAccountStore } from '../../components/Stores/accountStore';
import { createAccountWithId, getAccounts } from '@sp-firebase/accountFB';
import {
  Card,
  Select,
  MenuItem,
  Button,
  Input,
  CircularProgress,
  ListSubheader,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { AdminLoading } from './admin-components/AdminCommon';
import BasicTable from '../FinancialPositions/components/BasicTable';
import { getColDefs, genericFormatter } from '../FinancialPositions/components/BasicTableUtils';
import { SpotLockUser } from '../../sp-types/userType';
import { Account } from '../../sp-types/accountTypes';

import { PermittedUsers } from './admin-components/PermittedUsers';
import { UserPermissions } from './admin-components/UserPermissions';
import { LoadingSpinnerOverlay } from '../../components/UI-Elements/LoadingSpinnerOverlay';
import { EmailOutlined } from '@mui/icons-material';
import { sendEmail, useUser } from '@hooks/useUser';
import { toast } from 'react-toastify';
import { createEmptyInventory, emptyBatch, getEmptyAccountMaterials, mapMaterialToAccount } from './AdminUtils';
import { createInventory } from '../../sp-firebase/inventoryFB';
import { getAllUsers, updateUserAccount, updateUserProperty } from '../../sp-firebase/userFB';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { triggerExposureCalc } from '../../hooks/useExposure';
import { createMissingExposure } from '../../sp-firebase/exposureFB';
import { IRoute } from '../../sp-types';
import { getAllRoutes } from '../../sp-firebase/routesFB';
import { OverlayDisplayDiv } from '../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { AccountOptsPopup } from './admin-components/AccountOptsPopup';

export const AdminManageAccounts = () => {
  const { data: userData } = useUser();
  const { account } = useAccountStore();
  const { materials, materialsMap } = useMaterialsStore();
  const [selectedAccount, setSelectedAccount] = React.useState<Account | undefined>();
  const [newAccount, setNewAccount] = React.useState('');
  const [isDirty, setIsDirty] = React.useState(false);
  const [selectedMaterials, setSelectedMaterials] = React.useState<string[]>([]);

  const [accounts, setAccounts] = React.useState<Account[]>([]);

  const [gridItems, setGridItems] = React.useState<any[]>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [permittedUsers, setPermittedUsers] = React.useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = React.useState<boolean>(false);
  const [dbgMessage, setDbgMessage] = React.useState('');

  const [allRoutes, setAllRoutes] = React.useState<IRoute[]>([]);
  const [allUsers, setAllUsers] = React.useState<SpotLockUser[]>([]);
  const [selRoute, setSelRoute] = React.useState<string>('');
  const [materialsFilter, setMaterialsFilter] = React.useState<string>('');
  const materialsFilterRef = React.useRef<HTMLInputElement | null>(null);

  const filteredMaterials = React.useMemo(() => {
    if (!materialsFilter) return materials;
    const q = materialsFilter.toLowerCase();
    return materials.filter((mat) => mat.name.toLowerCase().includes(q) || mat.id.toLowerCase().includes(q));
  }, [materials, materialsFilter]);

  const highlightMatch = (text: string, queryText: string): React.ReactNode => {
    if (!queryText) return text;
    const q = queryText.toLowerCase();
    const lower = text.toLowerCase();
    const parts: React.ReactNode[] = [];
    let idx = 0;
    while (idx < text.length) {
      const hit = lower.indexOf(q, idx);
      if (hit === -1) {
        parts.push(text.slice(idx));
        break;
      }
      if (hit > idx) parts.push(text.slice(idx, hit));
      parts.push(<strong key={`${text}-${hit}`}>{text.slice(hit, hit + q.length)}</strong>);
      idx = hit + q.length;
    }
    return <>{parts}</>;
  };

  React.useEffect(() => {
    // keep focus on the filter input while typing
    materialsFilterRef.current?.focus();
  }, [materialsFilter]);

  const fetchRoutes = async () => {
    const _allRoutes = await getAllRoutes();

    setAllRoutes(_allRoutes || []);
  };

  const getAllAccounts = async () => {
    setLoading(true);
    const allAccounts = await getAccounts();

    setAccounts(allAccounts || []);
    setSelectedAccount(allAccounts?.[0]);

    setLoading(false);
  };

  React.useEffect(() => {
    getAllAccounts();
  }, [account]);

  React.useEffect(() => {
    if (selectedAccount) {
      setSelectedMaterials(selectedAccount.materialsToAccount.map(({ materialId }) => materialId).filter(Boolean));
      setUpdateAccountBtnTxt(`Update ${selectedAccount?.id} account`);

      fetchUsers().then(() => {
        setPermittedUsers(selectedAccount?.users || {});
      });

      fetchRoutes();
    }
  }, [selectedAccount]);

  const fetchAllUsers = async () => {
    setLoading(true);
    const users = await getAllUsers();

    setAllUsers(users || []);

    setLoading(false);
  };

  React.useEffect(() => {
    fetchAllUsers();
  }, []);

  const changeUserEdit = async (userId: string, prop: string, val: any) => {
    setDbgMessage(`Updating user`);
    setLoading(true);
    await updateUserProperty(userId, prop, val);

    await fetchAllUsers();
    setLoading(false);
  };
  const colDefs = ['id', 'accountId', 'email', 'isAdmin', 'isDemo'].reduce((res, col) => ({ ...res, [col]: true }), {});

  const columnFormatter = (data: any): React.ReactNode | undefined => {
    if (data?.column.Header === 'email') {
      return (
        <div className="flex gap-2">
          <p>{data?.value}</p>
          <p className="cursor-pointer">
            <Button onClick={() => emailUser(data?.value, data?.row.original?.id)}>
              <EmailOutlined />
            </Button>
          </p>
        </div>
      );
    }

    return genericFormatter(data);
  };

  const emailUser = async (email: string, uid: string) => {
    setLoading(true);
    try {
      await sendEmail(uid, email);
      toast.info('email sent');
    } catch (err: any) {
      console.log('Error sending email: ', err);
      toast.info(`Error, ${err?.message || err}`);
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    setLoading(true);

    const allUsers = await getAllItems<SpotLockUser>(
      'users',
      (q) => query(q, where('accountId', '==', selectedAccount?.id)),
      true,
    );
    setGridItems(allUsers || []);

    setLoading(false);
  };

  const blockUser = (userId: string) => {
    delete permittedUsers[userId];
    setPermittedUsers({ ...permittedUsers });
    setHasChanges(true);
  };

  const addUser = (userId: string, email: string) => {
    setPermittedUsers({ ...permittedUsers, [userId]: email });
    setHasChanges(true);
  };

  const addRoute = (routeId: string) => {
    if (!routeId || !selectedAccount) return;
    setSelectedAccount({
      ...selectedAccount,
      assignedRoutes: [...(selectedAccount?.assignedRoutes || []), routeId],
    });
    setSelRoute('');
    setHasChanges(true);
  };

  const blockRoute = (routeId: string) => {
    if (!selectedAccount?.assignedRoutes) {
      return;
    }

    setSelectedAccount({
      ...selectedAccount,
      assignedRoutes: selectedAccount.assignedRoutes.filter((r) => r !== routeId),
    });
    setHasChanges(true);
  };

  const save = async () => {
    setLoading(true);

    const updatedAccount = { ...account, users: permittedUsers, assignedRoutes: selectedAccount?.assignedRoutes };

    await saveItem<Account>(updatedAccount.id || '', updatedAccount, 'accounts');

    setLoading(false);
  };

  const createNewAccount = async () => {
    const myAccountId = newAccount || 'useCase8';
    const materialIds = ['general', ...selectedMaterials.sort().filter((a) => a !== 'general')];
    const materialsToAccount = mapMaterialToAccount(materialIds, emptyBatch);
    if (userData) {
      setLoading(true);
      const accountData = getEmptyAccountMaterials(myAccountId, userData, emptyBatch, materialsToAccount);
      setDbgMessage('Creating account...');
      await createAccountWithId(accountData, myAccountId);
      setDbgMessage('Updating user account');
      await updateUserAccount(userData.id, myAccountId);
      await createInventory(createEmptyInventory(myAccountId));
      setDbgMessage('Clear exposure');
      await createMissingExposure(myAccountId, selectedMaterials);
      setDbgMessage('Triggering exposure calc');
      await triggerExposureCalc({ accountId: myAccountId, materialIds: selectedMaterials });

      setLoading(false);
      setDbgMessage('');
      toast.info(`Account ${myAccountId} created`);
    }
  };
  const [updateAccountBtnTxt, setUpdateAccountBtnTxt] = React.useState<string>('Update account');
  const updateAccount = async () => {
    if (!selectedAccount) return;
    setIsDirty(false);
    setLoading(true);

    const materialsSet = new Set<string>(['general', ...selectedMaterials]);

    const existingMaterials = selectedAccount?.materialsToAccount.filter((m) => materialsSet.has(m.materialId)) || [];
    const existingIdsSet = new Set<string>(existingMaterials?.map(({ materialId }) => materialId) || []);
    const addedMaterials = selectedMaterials.filter((m) => !existingIdsSet.has(m));
    const materialsToAccount = mapMaterialToAccount(addedMaterials, emptyBatch);

    selectedAccount.materialsToAccount = [...existingMaterials, ...materialsToAccount];
    setDbgMessage('Updating account...');
    await createAccountWithId(selectedAccount, selectedAccount.id || '');

    setDbgMessage('adding exposure to new materials');
    await createMissingExposure(selectedAccount.id || '', selectedMaterials);

    setDbgMessage('Triggering exposure calc');
    await triggerExposureCalc({ accountId: selectedAccount.id || '', materialIds: [...materialsSet] });
    setDbgMessage('Completed successfully');
    setLoading(false);
    updateBtnText();
  };

  const updateBtnText = () => {
    let interval = 5;
    setUpdateAccountBtnTxt(`Updated successfully (${interval})`);
    const intervalPtr = setInterval(() => {
      interval--;
      if (interval > -1) {
        setUpdateAccountBtnTxt(`Updated successfully (${interval})`);
        return;
      }
      setUpdateAccountBtnTxt(`Update ${selectedAccount?.id} account`);

      clearInterval(intervalPtr);
    }, 1000);
  };

  const [showOptsPopup, setShowOptsPopup] = React.useState<boolean>(false);

  return accounts ? (
    <div className="flex flex-col flex-1 bg-white p-3">
      <div className="flex gap-3 align-middle items-center">
        manage users and accounts
        <Card className="flex flex-row gap-4 align-top h-12 m-1 p-2 shadow-custom ">
          Account:
          <Select label="Account" value={selectedAccount?.id || ''}>
            {accounts?.map((myAccount, i) => (
              <MenuItem
                key={i}
                id={myAccount.id}
                value={myAccount.id}
                onClick={() => {
                  setSelectedAccount(myAccount);
                  setIsDirty(false);
                }}
              >
                {myAccount.id}
              </MenuItem>
            ))}
          </Select>
          <Select
            className="w-56"
            title={selectedMaterials.join(', ')}
            label="Materials"
            value={selectedMaterials}
            multiple
            onOpen={() => setMaterialsFilter('')}
            MenuProps={{
              autoFocus: false,
              disableAutoFocusItem: true,
              PaperProps: { style: { maxHeight: 500, overflowY: 'auto' } },
            }}
            onChange={({ target }) => {
              setSelectedMaterials(typeof target.value === 'string' ? [target.value] : target.value);
              setIsDirty(true);
            }}
          >
            <ListSubheader>
              <Input
                placeholder="Filter materials..."
                value={materialsFilter}
                onChange={({ target }) => setMaterialsFilter(target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                fullWidth
                inputRef={materialsFilterRef}
                endAdornment={
                  materialsFilter && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setMaterialsFilter('');
                          materialsFilterRef.current?.focus();
                        }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              />
            </ListSubheader>
            {filteredMaterials.map((mat) => (
              <MenuItem key={mat.id} value={mat.id}>
                <span>
                  {highlightMatch(mat.name, materialsFilter)} ({highlightMatch(mat.id, materialsFilter)})
                </span>
              </MenuItem>
            ))}
          </Select>
          <Button onClick={() => setShowOptsPopup(true)} variant="contained" disabled={loading}>
            options <OpenInNew />
          </Button>
          {showOptsPopup && (
            <OverlayDisplayDiv setDisplayOverlay={() => setShowOptsPopup(false)} hideClose>
              <AccountOptsPopup close={() => setShowOptsPopup(false)} {...{ accounts, materials, materialsMap }} />
            </OverlayDisplayDiv>
          )}
          <Button onClick={updateAccount} variant="contained" disabled={!isDirty || !selectedMaterials.length}>
            {updateAccountBtnTxt}
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
          </Button>
        </Card>
        <Card className="flex flex-row gap-4 align-top h-12 m-1 p-2 shadow-custom ">
          <Input
            type="text"
            placeholder="enter Id"
            value={newAccount}
            onChange={({ target }) => setNewAccount(target.value)}
          ></Input>
          <Button onClick={createNewAccount} variant="contained" disabled={!newAccount || !selectedMaterials.length}>
            Create Empty account with ID:
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
          </Button>
        </Card>
      </div>
      {gridItems && (
        <div className="flex flex-col gap-2">
          <div className="flex h-[90%] overflow-auto">
            <BasicTable
              allRows={gridItems}
              columns={getColDefs(colDefs, columnFormatter).filter((c) => c.name !== 'ID')}
              options={{
                cellClass: 'max-h-24',
                noBorders: true,
                filters: { status: true, text: true },
                noDataMsg: 'No users',
              }}
            ></BasicTable>
          </div>
        </div>
      )}
      <div className="flex max-h-80 border-t border-b p-2 overflow-scroll">
        <div className="table">
          {selectedAccount?.assignedRoutes?.map((routeId, key) => (
            <div key={key} className="table-row">
              <div className="table-cell">{routeId}</div>
              <div className="table-cell cursor-pointer">
                <Button onClick={() => blockRoute(routeId)}>
                  <RemoveCircleOutline />
                </Button>
              </div>
            </div>
          ))}
          <div className="table-row">
            <div className="table-cell">
              <Select onChange={({ target }) => setSelRoute(target.value || '')} value={selRoute}>
                {allRoutes?.map((r, i) => (
                  <MenuItem key={i} value={r.id}>
                    {r.name} ({r.id})
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className="table-cell cursor-pointer">
              <Button onClick={() => addRoute(selRoute)}>
                <AddCircleOutline />
              </Button>
            </div>
          </div>
        </div>
      </div>
      {selectedAccount && (
        <div className="mt-4 border border-gray-110 p-2 gap-2">
          <div className="flex gap-2 font-bold">
            <p>Permitted users for account:</p>
            <p className="text-blue-300"> {selectedAccount?.id}</p>
            <p className={selectedAccount?.status === 'active' ? 'text-green-300' : 'text-red'}>
              {selectedAccount?.status}
            </p>
          </div>
          <PermittedUsers pUsers={permittedUsers} addUser={addUser} blockUser={blockUser} />
          {hasChanges && (
            <Button onClick={save} variant="contained">
              Save changes
            </Button>
          )}
        </div>
      )}
      <div className="mt-4 border border-gray-110 p-2 gap-2">
        <div className="flex gap-2 font-bold">
          <p>All users</p>
        </div>
        <UserPermissions users={allUsers} setUserProp={changeUserEdit} />
      </div>
      {loading && <LoadingSpinnerOverlay size={64}>{dbgMessage}</LoadingSpinnerOverlay>}
    </div>
  ) : (
    <AdminLoading />
  );
};
